import { useMemo } from 'react';
import { InboxLocationPhones, type InboxLocationPhonesTypes } from '@frontend/inbox-location-phones';
import { theme } from '@frontend/theme';
import { PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { PhoneOption } from './phone-option';

export type PhoneOptionsProps = {
  activeIndex: number;
  baseIndex: number;
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps'];
  onSelectLocationPhone: (locationPhone: string, locationId: string, threadId?: string) => void;
  phones: (InboxLocationPhonesTypes.LocationPhone & { threadId?: string })[];
  searchText?: string;
  selectedLocationPhone?: string;
};

export const PhoneOptions = ({
  activeIndex,
  baseIndex,
  getItemProps,
  onSelectLocationPhone,
  phones,
  searchText,
  selectedLocationPhone,
}: PhoneOptionsProps) => {
  const sortedPhones = useMemo(() => phones.sort(InboxLocationPhones.Utils.sortLocationPhones), [phones]);
  const filteredPhones = useMemo(() => {
    const normalizedSearch = searchText?.replace(/[^A-Za-z0-9]/g, '').toLowerCase();
    return normalizedSearch
      ? sortedPhones.filter(({ locationPhone, displayName }) => {
          const searchableString = `${displayName ?? ''} ${locationPhone}`.replace(/[^A-Za-z0-9]/g, '').toLowerCase();
          return searchableString.includes(normalizedSearch);
        }, [])
      : sortedPhones;
  }, [searchText, sortedPhones]);

  if (filteredPhones.length === 0) {
    return null;
  }

  return (
    <>
      {filteredPhones.map(({ defaultSms, displayName, locationId, locationPhone, threadId }, i) => {
        const index = i + baseIndex;

        return (
          <PopoverMenuItem
            key={`${locationPhone}-location-phone-option`}
            {...getItemProps({
              index,
              onClick: () => onSelectLocationPhone(locationPhone, locationId, threadId),
            })}
            trackingId='inbox-thread-new-message-location-phone-option'
            data-value={locationPhone}
            css={[
              displayName && {
                paddingTop: theme.spacing(0.5),
                paddingBottom: theme.spacing(0.5),
                minHeight: 'fit-content',
              },
              selectedLocationPhone === locationPhone && {
                borderLeft: `4px solid ${theme.colors.primary50}`,
              },
            ]}
          >
            <PhoneOption
              defaultSms={defaultSms}
              displayName={displayName}
              isActive={activeIndex === index}
              locationId={locationId}
              phoneNumber={locationPhone}
            />
          </PopoverMenuItem>
        );
      })}
    </>
  );
};
